/**
 * Apartsales — Icons.
 *
 * All tab-bar icons normalized for optical balance:
 *   - viewBox 24×24, drawable area ~17×17 centered
 *   - stroke 2pt, round caps + joins, currentColor
 *   - filled variants = solid silhouette only (no stroke)
 *   - inner cutouts on filled variants use var(--color-bg) (theme-aware)
 *   - NO elements protruding outside the main shape
 *     (e.g. AI sparkle sits INSIDE the lens, not in the corner)
 *
 * Verified bounding boxes (stroke included):
 *   HomeIcon       ~19 × 18
 *   FeedIcon       ~17 × 14
 *   AISearchIcon   ~17 × 17
 *   MapPinIcon     ~16 × 18
 *   HeartIcon      ~19 × 17
 *
 * No external icon libraries. All paths drawn from geometric primitives.
 */

import type { SVGProps } from 'react';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'width' | 'height'> {
  size?: number;
  filled?: boolean;
}

function svgProps({ size = 26, ...rest }: IconProps) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    'aria-hidden': true as const,
    ...rest,
  };
}

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/* ════════════════════════════════════════════════════════════════
 *  TAB BAR (5)
 * ═══════════════════════════════════════════════════════════════ */

/** Главная — pentagonal house */
export function HomeIcon(props: IconProps) {
  if (props.filled) {
    return (
      <svg {...svgProps(props)}>
        <path
          d="M11.32 2.46a1 1 0 0 1 1.36 0l8.68 8.5a1 1 0 0 1 .31.71v8.7c0 1-.8 1.83-1.81 1.83H15v-7.4a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1V22.2H4.14A1.81 1.81 0 0 1 2.33 20.37v-8.7c0-.27.11-.53.31-.71l8.68-8.5Z"
          fill="currentColor"
        />
      </svg>
    );
  }
  return (
    <svg {...svgProps(props)}>
      <path
        {...STROKE}
        d="m3 11.4 9-8.4 9 8.4v8.78a.83.83 0 0 1-.83.82H15v-7.5a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1V21H3.83a.83.83 0 0 1-.83-.82V11.4Z"
      />
    </svg>
  );
}

/**
 * Лента — 2×2 grid of rounded squares (iOS Collections / IG grid pattern).
 * Each square 7.5×7.5, rounded 1.6, with 1.5pt gap between. Reads as
 * "grid view of feed items" universally.
 */
export function FeedIcon(props: IconProps) {
  if (props.filled) {
    return (
      <svg {...svgProps(props)}>
        <rect x="3.5" y="3.5" width="7.5" height="7.5" rx="1.6" fill="currentColor" />
        <rect x="13" y="3.5" width="7.5" height="7.5" rx="1.6" fill="currentColor" />
        <rect x="3.5" y="13" width="7.5" height="7.5" rx="1.6" fill="currentColor" />
        <rect x="13" y="13" width="7.5" height="7.5" rx="1.6" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg {...svgProps(props)}>
      <rect {...STROKE} x="3.5" y="3.5" width="7.5" height="7.5" rx="1.6" />
      <rect {...STROKE} x="13" y="3.5" width="7.5" height="7.5" rx="1.6" />
      <rect {...STROKE} x="3.5" y="13" width="7.5" height="7.5" rx="1.6" />
      <rect {...STROKE} x="13" y="13" width="7.5" height="7.5" rx="1.6" />
    </svg>
  );
}

/**
 * Поиск (AI) — clean magnifier with a concave 4-point sparkle at top-right.
 *
 * The magnifier is slightly biased to bottom-left so the sparkle has room
 * top-right without overlapping. The sparkle uses concave (curved-inward)
 * sides between its 4 points, which makes it unambiguously read as a
 * "sparkle/twinkle/star" rather than a plus sign — a flat 4-point star at
 * small sizes flattens to crosshairs.
 */
export function AISearchIcon(props: IconProps) {
  const sparklePath =
    'M19 2c.06 0 .12.05.13.11l.4 2.18a1.4 1.4 0 0 0 1.18 1.18l2.18.4a.13.13 0 0 1 0 .26l-2.18.4a1.4 1.4 0 0 0-1.18 1.18l-.4 2.18a.13.13 0 0 1-.26 0l-.4-2.18a1.4 1.4 0 0 0-1.18-1.18l-2.18-.4a.13.13 0 0 1 0-.26l2.18-.4a1.4 1.4 0 0 0 1.18-1.18l.4-2.18A.13.13 0 0 1 19 2Z';

  if (props.filled) {
    return (
      <svg {...svgProps(props)}>
        <circle cx="10.5" cy="11.5" r="7" fill="currentColor" />
        <path
          d="m20 21-3.5-3.5"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          fill="none"
        />
        <path d={sparklePath} fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg {...svgProps(props)}>
      <circle {...STROKE} cx="10.5" cy="11.5" r="7" />
      <path {...STROKE} strokeWidth="1.9" d="m20 21-3.5-3.5" />
      <path d={sparklePath} fill="currentColor" />
    </svg>
  );
}

/**
 * Карта — geographic pin (teardrop), wider than before so its bbox aligns
 * with the rest. Inner circle hollow.
 */
export function MapPinIcon(props: IconProps) {
  if (props.filled) {
    return (
      <svg {...svgProps(props)}>
        <path
          d="M12 2.5c-4.7 0-8.5 3.6-8.5 8 0 5.85 7.45 11.95 7.85 12.27a1 1 0 0 0 1.3 0c.4-.32 7.85-6.42 7.85-12.27 0-4.4-3.8-8-8.5-8Z"
          fill="currentColor"
        />
        <circle cx="12" cy="10.2" r="2.8" fill="var(--color-bg)" />
      </svg>
    );
  }
  return (
    <svg {...svgProps(props)}>
      <path
        {...STROKE}
        d="M12 22c-1.6-1.5-7.5-7-7.5-12 0-4.14 3.36-7.5 7.5-7.5s7.5 3.36 7.5 7.5c0 5-5.9 10.5-7.5 12Z"
      />
      <circle {...STROKE} cx="12" cy="10.2" r="2.8" />
    </svg>
  );
}

/** Избранное — heart, smooth concave top, fills similar canvas as Home. */
export function HeartIcon(props: IconProps) {
  if (props.filled) {
    return (
      <svg {...svgProps(props)}>
        <path
          d="M12 21c-7.4-4.6-9.9-8.7-9.9-12.5 0-2.85 2.04-5.2 4.85-5.2 1.97 0 3.6 1.05 5.05 3.07 1.45-2.02 3.08-3.07 5.05-3.07 2.81 0 4.85 2.35 4.85 5.2 0 3.8-2.5 7.9-9.9 12.5Z"
          fill="currentColor"
        />
      </svg>
    );
  }
  return (
    <svg {...svgProps(props)}>
      <path
        {...STROKE}
        d="M12 21c-7.4-4.6-9.9-8.7-9.9-12.5 0-2.85 2.04-5.2 4.85-5.2 1.97 0 3.6 1.05 5.05 3.07 1.45-2.02 3.08-3.07 5.05-3.07 2.81 0 4.85 2.35 4.85 5.2 0 3.8-2.5 7.9-9.9 12.5Z"
      />
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════════
 *  TOP HEADER
 * ═══════════════════════════════════════════════════════════════ */

/** Корзина — shopping bag with curved handles.
 *
 *  cornerGlyph — рисует «+» или «−» в правом верхнем углу того же SVG,
 *  тем же stroke=2 rounded, что и outline корзины. То есть это один
 *  цельный иконографический glyph «Bag + Plus», не отдельный бэйдж.
 *  Цвет наследуется из currentColor (один со всей иконкой).
 *
 *  Для рендера corner glyph viewBox получает overflow="visible" — линии
 *  частично выходят за coordinate frame но это нормально, у chip нет
 *  overflow:hidden, glyph остаётся в пределах chip-площади.
 */
export function BagIcon(props: IconProps & { cornerGlyph?: 'plus' | 'minus' | null }) {
  const { cornerGlyph, ...rest } = props;
  const corner = cornerGlyph === 'plus' ? (
    <g>
      {/* horizontal — длина 3.5, центр (21.75, 3.25) */}
      <line x1="20" y1="3.25" x2="23.5" y2="3.25" {...STROKE} />
      {/* vertical */}
      <line x1="21.75" y1="1.5" x2="21.75" y2="5" {...STROKE} />
    </g>
  ) : cornerGlyph === 'minus' ? (
    <line x1="20" y1="3.25" x2="23.5" y2="3.25" {...STROKE} />
  ) : null;

  if (props.filled) {
    return (
      <svg {...svgProps(rest)} overflow="visible">
        <path
          d="M5.4 7h13.2a1 1 0 0 1 1 1.1l-1.3 12a1.5 1.5 0 0 1-1.5 1.4H7.2a1.5 1.5 0 0 1-1.5-1.4L4.4 8.1A1 1 0 0 1 5.4 7Z"
          fill="currentColor"
        />
        <path
          d="M8 8.5V6.5a4 4 0 0 1 8 0v2"
          stroke="var(--color-bg-base, #000)"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
        />
        {corner}
      </svg>
    );
  }
  return (
    <svg {...svgProps(rest)} overflow="visible">
      <path
        {...STROKE}
        d="M5.4 7.5h13.2a1 1 0 0 1 1 1.1l-1.2 11.4a1.5 1.5 0 0 1-1.5 1.4H7.1a1.5 1.5 0 0 1-1.5-1.4L4.4 8.6a1 1 0 0 1 1-1.1Z"
      />
      <path {...STROKE} d="M8 9V6.5a4 4 0 0 1 8 0V9" />
      {corner}
    </svg>
  );
}

/* ════════════════════════════════════════════════════════════════
 *  CARD ACTIONS / OTHER
 * ═══════════════════════════════════════════════════════════════ */

/**
 * Cart — supermarket trolley shape (vs Bag for top-bag-icon).
 * Used in the Action Bar under photo so it visually differs from the
 * top-header bag (which is the global cart). Same destination, different
 * affordance: "add this unit" vs "open my cart".
 */
/**
 * Cart — Apple-style shopping bag (SF Symbol "bag").
 * Rounded-corner bag body with two arched handles on top.
 * Matches apple.com header cart for iOS-native feel.
 */
export function CartIcon(props: IconProps) {
  if (props.filled) {
    return (
      <svg {...svgProps(props)}>
        {/* Bag body filled */}
        <path
          d="M5 8h14a0.5 0.5 0 0 1 0.5 0.5v11.5a1.5 1.5 0 0 1-1.5 1.5H6a1.5 1.5 0 0 1-1.5-1.5V8.5A0.5 0.5 0 0 1 5 8z"
          fill="currentColor"
        />
        {/* Handles outline (kept stroked for visual definition even when filled) */}
        <path
          d="M8.5 8V6.5a3.5 3.5 0 0 1 7 0V8"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.6}
          strokeLinecap="round"
        />
      </svg>
    );
  }
  return (
    <svg {...svgProps(props)}>
      {/* Bag body */}
      <path
        d="M5 8h14a0.5 0.5 0 0 1 0.5 0.5v11.5a1.5 1.5 0 0 1-1.5 1.5H6a1.5 1.5 0 0 1-1.5-1.5V8.5A0.5 0.5 0 0 1 5 8z"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinejoin="round"
      />
      {/* Two-handle arc */}
      <path
        d="M8.5 8V6.5a3.5 3.5 0 0 1 7 0V8"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Compare — two bars/columns with a divider, suggests side-by-side.
 * Adds the unit to /compare (doc 28 Comparison & Matchup).
 */
export function CompareIcon(props: IconProps) {
  if (props.filled) {
    return (
      <svg {...svgProps(props)}>
        <rect x="3.5" y="6" width="6.5" height="14" rx="1.5" fill="currentColor" />
        <rect x="14" y="3" width="6.5" height="17" rx="1.5" fill="currentColor" opacity="0.5" />
        <path d="M11.75 4v18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg {...svgProps(props)}>
      <rect {...STROKE} x="3.5" y="6" width="6.5" height="14" rx="1.5" />
      <rect {...STROKE} x="14" y="3" width="6.5" height="17" rx="1.5" />
      <path {...STROKE} d="M11.75 4v18" />
    </svg>
  );
}

/**
 * Bell — for "уведомить о цене" / price-drop alerts (doc 06 #38 +
 * #40 Time Machine watchlist).
 */
export function BellIcon(props: IconProps) {
  if (props.filled) {
    return (
      <svg {...svgProps(props)}>
        <path
          d="M12 2.5a6.5 6.5 0 0 0-6.5 6.5v3.6L4 16h16l-1.5-3.4V9A6.5 6.5 0 0 0 12 2.5Z"
          fill="currentColor"
        />
        <path
          d="M9.5 18a2.5 2.5 0 0 0 5 0"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    );
  }
  return (
    <svg {...svgProps(props)}>
      <path
        {...STROKE}
        d="M12 3a6 6 0 0 0-6 6v3.6L4.5 16h15L18 12.6V9a6 6 0 0 0-6-6Z"
      />
      <path {...STROKE} d="M10 18a2 2 0 0 0 4 0" />
    </svg>
  );
}

export function ChatIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path
        {...STROKE}
        d="M4 12c0-4 3.5-7.5 8-7.5s8 3.5 8 7.5-3.5 7.5-8 7.5c-1.2 0-2.4-.2-3.4-.7L4 20l1.2-3.6A7.4 7.4 0 0 1 4 12Z"
      />
    </svg>
  );
}

export function LockIcon(props: IconProps) {
  if (props.filled) {
    return (
      <svg {...svgProps(props)}>
        <rect x="5" y="11" width="14" height="9" rx="2.2" fill="currentColor" />
        <path
          {...STROKE}
          stroke="var(--color-bg-base, #000)"
          d="M8 11V8a4 4 0 0 1 8 0v3"
        />
      </svg>
    );
  }
  return (
    <svg {...svgProps(props)}>
      <rect {...STROKE} x="5" y="11" width="14" height="9" rx="2.2" />
      <path {...STROKE} d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

export function BookmarkIcon(props: IconProps) {
  if (props.filled) {
    return (
      <svg {...svgProps(props)}>
        <path d="M5 3h14a1 1 0 0 1 1 1v17l-8-5-8 5V4a1 1 0 0 1 1-1Z" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg {...svgProps(props)}>
      <path {...STROKE} d="M5.5 3.5h13a.5.5 0 0 1 .5.5v17l-7-4.5-7 4.5V4a.5.5 0 0 1 .5-.5Z" />
    </svg>
  );
}

export function ChevronLeftIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path {...STROKE} d="m15 6-6 6 6 6" />
    </svg>
  );
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path {...STROKE} d="m9 6 6 6-6 6" />
    </svg>
  );
}

export function PlusIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path {...STROKE} d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path {...STROKE} d="M6 6l12 12M18 6l-12 12" />
    </svg>
  );
}

/** Share — square with up arrow (iOS share sheet glyph) */
export function ShareIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path {...STROKE} d="M12 3v13M8 7l4-4 4 4" />
      <path {...STROKE} d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    </svg>
  );
}

/** Sparkle — 4-point star, used as "AI" accent next to text */
export function SparkleIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path
        d="M12 3c.07 0 .14.05.16.12l1 3.7a1.7 1.7 0 0 0 1.18 1.18l3.7 1a.17.17 0 0 1 0 .33l-3.7 1a1.7 1.7 0 0 0-1.18 1.18l-1 3.7a.17.17 0 0 1-.33 0l-1-3.7a1.7 1.7 0 0 0-1.18-1.18l-3.7-1a.17.17 0 0 1 0-.33l3.7-1a1.7 1.7 0 0 0 1.18-1.18l1-3.7c.02-.07.09-.12.16-.12Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Alias — same icon as AISearchIcon, kept for back-compat with SearchBar */
export const SearchSparkleIcon = AISearchIcon;

/** Star — 5-point classic star, used for ratings */
export function StarIcon(props: IconProps) {
  if (props.filled) {
    return (
      <svg {...svgProps(props)}>
        <path
          d="M12 2.5c.18 0 .35.1.43.27l2.6 5.27c.06.13.18.22.32.24l5.82.85c.4.06.55.55.27.84l-4.21 4.1c-.1.1-.15.24-.13.39l1 5.8c.07.4-.35.7-.7.51l-5.2-2.74a.51.51 0 0 0-.48 0l-5.2 2.74c-.36.19-.78-.11-.7-.51l.99-5.8a.51.51 0 0 0-.13-.39l-4.21-4.1c-.29-.29-.13-.78.27-.84l5.82-.85a.51.51 0 0 0 .32-.24l2.6-5.27c.07-.18.24-.27.42-.27Z"
          fill="currentColor"
        />
      </svg>
    );
  }
  return (
    <svg {...svgProps(props)}>
      <path
        {...STROKE}
        d="M12 2.5c.18 0 .35.1.43.27l2.6 5.27c.06.13.18.22.32.24l5.82.85c.4.06.55.55.27.84l-4.21 4.1c-.1.1-.15.24-.13.39l1 5.8c.07.4-.35.7-.7.51l-5.2-2.74a.51.51 0 0 0-.48 0l-5.2 2.74c-.36.19-.78-.11-.7-.51l.99-5.8a.51.51 0 0 0-.13-.39l-4.21-4.1c-.29-.29-.13-.78.27-.84l5.82-.85a.51.51 0 0 0 .32-.24l2.6-5.27c.07-.18.24-.27.42-.27Z"
      />
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────── */
/*  Search-screen icons (added in Search rewrite)                 */
/* ────────────────────────────────────────────────────────────── */

/** Filter — sliders (горизонтальные регуляторы) */
export function FilterIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path
        {...STROKE}
        d="M3 7h11M3 12h6m6 0h6M3 17h13"
      />
      <circle {...STROKE} cx="16" cy="7" r="2" fill="currentColor" />
      <circle {...STROKE} cx="11" cy="12" r="2" fill="currentColor" />
      <circle {...STROKE} cx="18" cy="17" r="2" fill="currentColor" />
    </svg>
  );
}

/** Map — сложенная карта с pin */
export function MapIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path {...STROKE} d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2Z" />
      <path {...STROKE} d="M9 4v14M15 6v14" />
    </svg>
  );
}

/** List — горизонтальные строки */
export function ListIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path {...STROKE} d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  );
}

/** Flame — trending */
export function FlameIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path
        {...STROKE}
        d="M12 3c1 4 5 5 5 10a5 5 0 1 1-10 0c0-2 1-3 2-4-1 3 1 4 2 4 0-3-1-5 1-10Z"
      />
    </svg>
  );
}

/** Clock — recent */
export function ClockIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <circle {...STROKE} cx="12" cy="12" r="9" />
      <path {...STROKE} d="M12 7v5l3 2" />
    </svg>
  );
}

/** Sliders (vertical) — sort */
export function SortIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path {...STROKE} d="M7 4v16M7 20l-3-3M7 20l3-3" />
      <path {...STROKE} d="M17 20V4M17 4l-3 3M17 4l3 3" />
    </svg>
  );
}

/** Сетка 4 квадрата — Compare */
export function GridIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <rect {...STROKE} x="4" y="4" width="7" height="7" rx="1.5" />
      <rect {...STROKE} x="13" y="4" width="7" height="7" rx="1.5" />
      <rect {...STROKE} x="4" y="13" width="7" height="7" rx="1.5" />
      <rect {...STROKE} x="13" y="13" width="7" height="7" rx="1.5" />
    </svg>
  );
}

/** House — preset Семья */
export function HouseIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path {...STROKE} d="M4 11 12 4l8 7v9a1 1 0 0 1-1 1h-4v-7H9v7H5a1 1 0 0 1-1-1v-9Z" />
    </svg>
  );
}

/** Briefcase — preset Инвестиции */
export function BriefcaseIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <rect {...STROKE} x="3" y="7" width="18" height="13" rx="2" />
      <path {...STROKE} d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
      <path {...STROKE} d="M3 13h18" />
    </svg>
  );
}

/** Beach / Sun — preset Курорт */
export function BeachIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <circle {...STROKE} cx="12" cy="9" r="3.5" />
      <path {...STROKE} d="M12 2.5v1.5M12 14v1.5M5.5 9h-1.5M20 9h-1.5M7.5 4.5l-1-1M16.5 4.5l1-1M7.5 13.5l-1 1M16.5 13.5l1 1M3 20h18" />
    </svg>
  );
}

/** Building — preset Бизнес */
export function BuildingIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <rect {...STROKE} x="5" y="3" width="14" height="18" rx="1.5" />
      <path {...STROKE} d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2" />
      <path {...STROKE} d="M10 21v-3h4v3" />
    </svg>
  );
}

/** Plus circle — preset «Свой» */
export function PlusCircleIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <circle {...STROKE} cx="12" cy="12" r="9" />
      <path {...STROKE} d="M12 8v8M8 12h8" />
    </svg>
  );
}

/** Microphone — voice (used as visual placeholder, no functionality yet) */
export function MicIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <rect {...STROKE} x="9" y="3" width="6" height="12" rx="3" />
      <path {...STROKE} d="M5 11a7 7 0 0 0 14 0M12 18v3M9 21h6" />
    </svg>
  );
}

/** Camera — photo search */
export function CameraIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path {...STROKE} d="M4 7h3l1.5-2h7L17 7h3v12H4V7Z" />
      <circle {...STROKE} cx="12" cy="13" r="3.5" />
    </svg>
  );
}

/** Tune — расширенный фильтр */
export function TuneIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path {...STROKE} d="M5 6h6M13 6h6M5 12h2M9 12h10M5 18h10M17 18h2" />
      <circle {...STROKE} cx="12" cy="6" r="2" fill="currentColor" />
      <circle {...STROKE} cx="8" cy="12" r="2" fill="currentColor" />
      <circle {...STROKE} cx="16" cy="18" r="2" fill="currentColor" />
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────── */
/*  Hero-form icons (added in Aviasales-style search rework)      */
/* ────────────────────────────────────────────────────────────── */

/** Calendar — for "Когда готов" */
export function CalendarIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <rect {...STROKE} x="3" y="5" width="18" height="16" rx="2.5" />
      <path {...STROKE} d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  );
}

/** Wallet — for "Бюджет" */
export function WalletIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path {...STROKE} d="M3 7v10a3 3 0 0 0 3 3h13a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2H6a3 3 0 0 1-3-3v0a3 3 0 0 1 3-3h11" />
      <circle cx="17" cy="13" r="1.5" fill="currentColor" />
    </svg>
  );
}

/** User — for "Кому" */
export function UserIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <circle {...STROKE} cx="12" cy="8" r="4" />
      <path {...STROKE} d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
    </svg>
  );
}

/** Arrow-right — CTA button */
export function ArrowRightIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path {...STROKE} d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

/** Globe — "Куда угодно" wildcard */
export function GlobeIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <circle {...STROKE} cx="12" cy="12" r="9" />
      <path {...STROKE} d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </svg>
  );
}

/** Trending-up — sparkline forecast */
export function TrendingUpIcon(props: IconProps) {
  return (
    <svg {...svgProps(props)}>
      <path {...STROKE} d="M3 17l6-6 4 4 8-8M14 7h7v7" />
    </svg>
  );
}
